import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { askPlanner } from '@platform/client';
import { isOnboardingProposal } from '@platform/schema/chat';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';

import { ChangeTable } from './ChangeTable';
import { exchangeCost } from './chat';
import { Commentary } from './Commentary';
import { sceneKindName } from './edits';
import {
  proposalRefusal,
  proposalsFrom,
  proposalWrite,
  proposedChanges,
} from './proposal';

import type { FieldWrite, LessonWrite } from './edits';
import type { AskPlannerResponse } from '@platform/client';
import type { Change } from '@platform/schema/changes';
import type {
  AskPlanner,
  ChatTurn,
  LessonProposal,
  OnboardingProposal,
  ProposedEdit,
} from '@platform/schema/chat';
import type { LessonWorkspace } from '@platform/schema/review';

/**
 * A chat with the planner — GEN-52, the edits it suggests — GEN-66, the path it can be
 * asked about — PATH-01, and several lessons at once — GEN-70.
 *
 * `askPlanner` is the only endpoint this file reaches and it is a read: applying a
 * proposal is `useEditing.save`, the same call the form makes, so it is a hand edit in
 * every way that matters — `docs/decisions/planner-proposals.md`. Several lessons is one
 * question with several answers rather than a bulk write: each is settled on its own.
 *
 * A collection subject carries no proposal at all:
 * `docs/decisions/path-chat-proposes-nothing.md`.
 */

/**
 * What the operator is looking at, and what can be done about the answer.
 *
 * One union rather than four props that have to agree. Flattened — `lessonId`, `noun`,
 * `lesson`, `onApply` — a collection could be passed a lesson's `onApply`, and the empty
 * state had to read `onApply === undefined` as *this is a collection*, which is a
 * callback's absence standing in for a subject's kind. A lesson screen that legitimately
 * had nothing to save would then have started describing itself as a path.
 *
 * `askPlannerSchema` refuses both ids at once on the wire; this is the same rule at the
 * component boundary, where the compiler can hold it.
 */
export type ChatSubject =
  | {
      kind: 'lesson';
      /** What is offered. Known before the payloads land, so not derived from them. */
      lessonIds: readonly string[];
      /** The lessons as they stand — a proposal's "Was" column is read from them. */
      lessons: readonly LessonWorkspace[];
      onApply: (write: LessonWrite) => void;
      /** Several at once, as one undo step — ADMIN-27. */
      onApplyAll: (writes: readonly FieldWrite[]) => void;
      /** The scene open beside the chat, which is what a question is about — GEN-97. */
      focus?: SceneRef | undefined;
      /** Back to the whole lesson. Absent where nothing can be focused. */
      onUnfocus?: () => void;
    }
  | {
      kind: 'collection';
      collectionId: string;
      /** What this kind is called in a sentence — "path", "course", "module". */
      noun: string;
    }
  | {
      /** One onboarding screen — ONB-21. Each screen keeps its own conversation. */
      kind: 'onboarding';
      stepId: string;
      /** What the step list calls it. */
      name: string;
      /** The diff and the apply for a proposal, against the page's unsaved config. */
      review: (edit: OnboardingProposal) => OnboardingReview;
      /** Several at once, folded into the draft in one go — ADMIN-27. */
      applyAll: (edits: readonly OnboardingProposal[]) => void;
    };

/** What an onboarding page supplies for one proposal; the card adds the rest. */
export type OnboardingReview = Pick<Review, 'changes' | 'apply'>;

type SceneRef = NonNullable<AskPlanner['scene']>;

/**
 * The focused scene as the lesson now holds it, so the label follows an edit and a move.
 * `undefined` for a scene the lesson no longer has, which leaves the whole lesson.
 */
const openScene = (subject: ChatSubject) => {
  if (subject.kind !== 'lesson' || subject.focus === undefined)
    return undefined;

  const { lessonId, sceneId } = subject.focus;
  const scene = subject.lessons
    .find((one) => one.id === lessonId)
    ?.scenes.find((one) => one.id === sceneId);

  return scene && { ref: subject.focus, scene };
};

/** The body `askPlanner` takes, which is the id and nothing else about the subject. */
const offering = (subject: ChatSubject) =>
  subject.kind === 'lesson'
    ? { lessonIds: [...subject.lessonIds] }
    : subject.kind === 'onboarding'
      ? { onboarding: { stepId: subject.stepId } }
      : { collectionId: subject.collectionId };

/** One stable empty transcript, so `Transcript`'s `memo` holds on a fresh conversation. */
const NOTHING_SAID: Said[] = [];

/** One conversation changed, the rest left as they were. */
const amend = (
  all: Readonly<Record<string, Said[]>>,
  at: string,
  change: (before: Said[]) => Said[],
) => ({ ...all, [at]: change(all[at] ?? []) });

/** What became of a proposal. Absent while it is still the operator's to decide. */
type Settled = 'applied' | 'dismissed';

/** One turn, and — for an answer — what it cost to get. */
interface Said {
  readonly turn: ChatTurn;
  readonly answer?: AskPlannerResponse;
  /** By position in the answer's proposals. Absent entries are still the operator's. */
  readonly settled?: Readonly<Record<number, Settled>>;
}

const DISMISSED = 'Dismissed. Nothing was written.';

/**
 * What the lesson undo says once applied — GEN-77. The arc is the one kind ⌘Z does not
 * take back, because `placeScenes` records no step for the Add and Remove controls either.
 */
const lessonApplied = (edit: LessonProposal): string =>
  edit.kind === 'arc'
    ? 'Applied. Which scenes a lesson has is not on the undo stack — put it back with the Add and Remove controls.'
    : 'Applied. Undo it with ⌘Z, or the Undo button above the lesson.';

/**
 * A proposal as one card shows it, whatever it edits. Derived on every render rather
 * than captured when the answer arrived, so "Was" is what the subject says now. An
 * operator who edited the field by hand in between sees that, instead of applying
 * against a value that has moved.
 */
interface Review {
  /** `undefined` for a row the subject no longer has. */
  readonly changes: readonly Change[] | undefined;
  readonly noun: string;
  readonly intro: string;
  readonly refused?: string | undefined;
  readonly applied: string;
  readonly apply: () => void;
}

/** The lesson the edit names, not the one on screen — GEN-70. */
function lessonReview(
  edit: LessonProposal,
  lessons: readonly LessonWorkspace[],
  onApply: (write: LessonWrite) => void,
): Review {
  const lesson = lessons.find((one) => one.id === edit.lessonId);

  return {
    changes: lesson && proposedChanges(lesson, edit),
    noun: 'lesson',
    intro:
      lessons.length === 1 || lesson === undefined
        ? 'The planner suggests this edit. Nothing is written until you apply it.'
        : `The planner suggests this edit to “${lesson.title}”. Nothing is written until you apply it.`,
    // A bound the write itself does not check — GEN-77. Shown beside the diff rather than
    // instead of it: what the change would have done is more use than that it is refused.
    refused: lesson && proposalRefusal(lesson, edit),
    applied: lessonApplied(edit),
    apply: () => {
      onApply(proposalWrite(edit));
    },
  };
}

/** One proposal, and the two ways out of it. */
function Proposal({
  review,
  settled,
  disabled,
  onApply,
  onDismiss,
}: {
  review: Review;
  settled: Settled | undefined;
  disabled: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  if (settled !== undefined)
    return (
      <Alert severity={settled === 'applied' ? 'success' : 'info'}>
        {settled === 'applied' ? review.applied : DISMISSED}
      </Alert>
    );

  const { changes, refused } = review;

  if (changes === undefined)
    return (
      <Alert severity="warning">
        The planner suggested an edit to something the {review.noun} no longer
        has. Ask again to get one against the {review.noun} as it stands.
      </Alert>
    );

  if (changes.length === 0)
    return (
      <Alert severity="info">
        The suggested edit is what the {review.noun} already says.
      </Alert>
    );

  return (
    <Stack spacing={1}>
      <Alert severity="info">{review.intro}</Alert>

      <ChangeTable changes={changes} />

      {refused !== undefined && <Alert severity="warning">{refused}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          disabled={disabled || refused !== undefined}
          onClick={() => {
            review.apply();
            onApply();
          }}
        >
          Apply
        </Button>
        <Button size="small" onClick={onDismiss}>
          Dismiss
        </Button>
      </Stack>
    </Stack>
  );
}

/** What can be done with a proposal on this subject, or nothing for a collection. */
type Reviewer = (edit: ProposedEdit) => Review | undefined;

/** A proposal still the operator's to decide, by where it sits in the conversation. */
interface Open {
  readonly at: number;
  readonly which: number;
  readonly edit: ProposedEdit;
}

/**
 * What Apply all would apply: what each card's own Apply would, less the arc, which has
 * no undo step to share — `docs/decisions/planner-proposals.md`.
 */
function openProposals(said: readonly Said[], reviewer: Reviewer): Open[] {
  return said.flatMap((one, at) =>
    one.answer === undefined
      ? []
      : proposalsFrom(one.answer).flatMap((edit, which) => {
          if (one.settled?.[which] !== undefined || edit.kind === 'arc')
            return [];

          const review = reviewer(edit);
          const ready =
            review?.changes !== undefined &&
            review.changes.length > 0 &&
            review.refused === undefined;

          return ready ? [{ at, which, edit }] : [];
        }),
  );
}

const ONBOARDING_REVIEW = {
  noun: 'screen',
  intro:
    'The planner suggests this edit. It goes into your unsaved changes; nothing is saved until you save the draft.',
  applied:
    'Applied to your unsaved changes. Save the draft to keep it, or edit it back on the left.',
};

/**
 * Its own component so typing does not re-render it: the draft question changes on every
 * keystroke and the transcript behind it does not.
 */
const Transcript = memo(function Transcript({
  said,
  conversation,
  disabled,
  onSettle,
  reviewer,
}: {
  said: Said[];
  disabled: boolean;
  /** Which conversation `said` is, passed back so `onSettle` needs no closure over it. */
  conversation: string;
  onSettle: (
    conversation: string,
    at: number,
    which: number,
    settled: Settled,
  ) => void;
  /** Absent where there is nothing to apply a proposal to — PATH-01. */
  reviewer: Reviewer | undefined;
}) {
  return (
    <>
      {said.map((one, at) => (
        <Paper
          // Turns are only ever appended, so the index is stable for the life of a
          // conversation — and there is no id to key on, because nothing is stored.
          key={at}
          variant="outlined"
          sx={{ p: 1 }}
        >
          <Commentary author={one.turn.role === 'operator' ? 'You' : 'Planner'}>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {one.turn.text}
            </Typography>

            {one.answer !== undefined && (
              <Typography variant="caption" color="text.secondary">
                {exchangeCost(one.answer)}
              </Typography>
            )}
          </Commentary>

          {one.answer !== undefined &&
            reviewer !== undefined &&
            proposalsFrom(one.answer).map((edit, which) => {
              const review = reviewer(edit);
              return (
                review && (
                  <Proposal
                    // Proposals are a fixed list on a settled answer, so the position
                    // is stable — and two edits to one scene are not distinguishable by id.
                    key={which}
                    review={review}
                    settled={one.settled?.[which]}
                    disabled={disabled}
                    onApply={() => {
                      onSettle(conversation, at, which, 'applied');
                    }}
                    onDismiss={() => {
                      onSettle(conversation, at, which, 'dismissed');
                    }}
                  />
                )
              );
            })}
        </Paper>
      ))}
    </>
  );
});

export function ChatPanel({
  subject,
  disabled = false,
}: {
  subject: ChatSubject;
  disabled?: boolean;
}) {
  /**
   * One conversation per scene, and one for the whole subject under `''` — GEN-97.
   *
   * Switching scene switches which one is shown and discards none: returning to Scene 7
   * shows the Scene 7 discussion, and nothing said about another scene is re-sent as if
   * it were about this one. Keyed by scene id, so a moved scene keeps its conversation.
   */
  const [conversations, setConversations] = useState<
    Readonly<Record<string, Said[]>>
  >({});
  const [question, setQuestion] = useState('');
  const [offered, setOffered] = useState(true);

  const open = openScene(subject);
  const key =
    open?.ref.sceneId ?? (subject.kind === 'onboarding' ? subject.stepId : '');
  const said = conversations[key] ?? NOTHING_SAID;

  const noun =
    subject.kind === 'collection'
      ? subject.noun
      : subject.kind === 'onboarding'
        ? 'screen'
        : subject.lessonIds.length === 1
          ? 'lesson'
          : `${String(subject.lessonIds.length)} lessons`;

  // Built from the subject's parts rather than the subject, which a parent passes as a
  // fresh object each render: keyed on that, `Transcript`'s `memo` would never hold.
  const lessons = subject.kind === 'lesson' ? subject.lessons : undefined;
  const applyWrite = subject.kind === 'lesson' ? subject.onApply : undefined;
  const applyWrites =
    subject.kind === 'lesson' ? subject.onApplyAll : undefined;
  const reviewScreen =
    subject.kind === 'onboarding' ? subject.review : undefined;
  const applyScreen =
    subject.kind === 'onboarding' ? subject.applyAll : undefined;
  const reviewer = useMemo((): Reviewer | undefined => {
    if (reviewScreen !== undefined)
      return (edit) =>
        isOnboardingProposal(edit)
          ? { ...ONBOARDING_REVIEW, ...reviewScreen(edit) }
          : undefined;
    if (lessons === undefined || applyWrite === undefined) return undefined;
    return (edit) =>
      isOnboardingProposal(edit)
        ? undefined
        : lessonReview(edit, lessons, applyWrite);
  }, [lessons, applyWrite, reviewScreen]);

  const applyAll = useCallback(
    (edits: readonly ProposedEdit[]) => {
      applyScreen?.(edits.filter(isOnboardingProposal));
      applyWrites?.(
        edits.flatMap((edit) => {
          if (isOnboardingProposal(edit)) return [];
          const write = proposalWrite(edit);
          return write.kind === 'arc' ? [] : [write];
        }),
      );
    },
    [applyScreen, applyWrites],
  );

  // Memoised for the same reason as `settle`: each keystroke re-renders this, and this
  // derives the diff of every proposal in the conversation.
  const openEdits = useMemo(
    () => (reviewer === undefined ? [] : openProposals(said, reviewer)),
    [said, reviewer],
  );

  const update = (at: string, change: (before: Said[]) => Said[]): void => {
    setConversations((before) => amend(before, at, change));
  };

  // `useCallback` so `Transcript`'s `memo` actually holds. Typing re-renders this
  // component on every keystroke, and a fresh `onSettle` each time would re-render the
  // whole transcript — re-parsing every past proposal and re-deriving its diff.
  const settle = useCallback(
    (
      conversation: string,
      at: number,
      which: number,
      settled: Settled,
    ): void => {
      setConversations((before) =>
        amend(before, conversation, (said) =>
          said.map((one, index) =>
            index === at
              ? { ...one, settled: { ...one.settled, [which]: settled } }
              : one,
          ),
        ),
      );
    },
    [],
  );

  const ask = useTrackedMutation('Ask the planner', {
    mutationFn: async (text: string) => {
      // The conversation it was asked in is the one the answer lands in, even if the
      // operator has moved to another scene by the time it arrives.
      const where = key;
      const scene = open?.ref;
      const asked: Said = { turn: { role: 'operator', text } };

      // Built from `said` rather than read back after the append, because state has not
      // settled by the time this runs and the operator's own question would be missing
      // from the transcript the planner is asked to answer.
      const turns = [...said.map((one) => one.turn), asked.turn];

      update(where, (before) => [...before, asked]);

      try {
        const answered = await askPlanner({
          body: {
            turns,
            ...(offered ? offering(subject) : {}),
            ...(scene === undefined ? {} : { scene }),
          },
          throwOnError: true,
        });

        return { answer: answered.data, where };
      } catch (failure) {
        // Take the question back out — left in, it would be re-sent with every later
        // turn, a question the planner never saw asked again forever. Removed by identity
        // rather than by position, so it does not rely on being the last entry.
        // `ActionFeed` is where the operator reads what went wrong.
        update(where, (before) => before.filter((one) => one !== asked));
        setQuestion(text);
        throw failure;
      }
    },
    onSuccess: ({ answer, where }) => {
      update(where, (before) => [
        ...before,
        { turn: { role: 'planner', text: answer.text }, answer },
      ]);
    },
  });

  const ready = question.trim() !== '' && !ask.isPending;

  /**
   * The transcript scrolls, the composer does not — GEN-69.
   *
   * The panel used to scroll whole, so asking a second question meant scrolling back down
   * to find the box you had just typed in. Following the newest turn is the other half:
   * an answer that arrives below the fold is one the operator is not told about.
   *
   * Keyed on the count and not the array: settling a proposal rewrites `said` in place, and
   * applying one read halfway up the history should not throw the operator to the bottom.
   */
  const transcript = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const pane = transcript.current;
    if (pane === null) return;

    pane.scrollTop = pane.scrollHeight;
  }, [said.length]);

  return (
    <Stack
      spacing={1}
      // The floor is what the composer needs, not what the transcript would like.
      // At 18rem it outranked the editor's flex share on the review screen once the
      // preview reserved its 390px, and the pane meant to get the width became the
      // narrowest on the page — ADMIN-10.
      sx={{ flex: 1, minWidth: '14rem', minHeight: 0, overflow: 'hidden' }}
    >
      <Typography variant="h6">Ask the planner</Typography>

      {/* What the planner is reviewing, so the operator can check it — GEN-97. Only
          where a scene can be opened; elsewhere the checkbox already names the subject. */}
      {subject.kind === 'onboarding' && (
        <Typography variant="subtitle2" aria-live="polite">
          Discussing: {subject.name}
        </Typography>
      )}

      {subject.kind === 'lesson' && subject.onUnfocus !== undefined && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }} aria-live="polite">
            <Typography variant="subtitle2">
              Discussing:{' '}
              {open === undefined
                ? 'the whole lesson'
                : `Scene ${String(open.scene.position)} — ${sceneKindName(open.scene.kind)}`}
            </Typography>
            {open !== undefined && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {open.scene.prompt}
              </Typography>
            )}
          </Box>
          {open !== undefined && (
            <Button size="small" onClick={subject.onUnfocus}>
              Whole lesson
            </Button>
          )}
        </Stack>
      )}

      <Stack
        ref={transcript}
        role="log"
        aria-label="Conversation"
        spacing={1}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        {said.length === 0 && (
          <Alert severity="info">
            Ask why something was written the way it was, or for wording you can
            use.{' '}
            {subject.kind === 'collection'
              ? `Nothing here is written: the planner answers about this ${noun}, and any change to it is yours to make above.`
              : 'When the answer comes down to concrete edits, the planner offers them — you see what each would change, and nothing is written unless you apply it.'}
          </Alert>
        )}

        <Transcript
          said={said}
          disabled={disabled}
          conversation={key}
          onSettle={settle}
          reviewer={reviewer}
        />
      </Stack>

      {openEdits.length > 1 && (
        <Button
          variant="contained"
          size="small"
          disabled={disabled}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => {
            applyAll(openEdits.map((one) => one.edit));
            for (const { at, which } of openEdits)
              settle(key, at, which, 'applied');
          }}
        >
          Apply all ({openEdits.length})
        </Button>
      )}

      {ask.isPending && <LinearProgress />}

      <TextField
        label="Your question"
        value={question}
        size="small"
        fullWidth
        multiline
        onChange={(event) => {
          setQuestion(event.target.value);
        }}
      />

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={offered}
            onChange={(event) => {
              setOffered(event.target.checked);
            }}
          />
        }
        label={
          subject.kind === 'lesson' && subject.lessonIds.length > 1
            ? `Show the planner these ${noun}`
            : `Show the planner this ${noun}`
        }
      />

      <Button
        variant="outlined"
        disabled={!ready}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          ask.mutate(question.trim());
          setQuestion('');
        }}
      >
        Ask
      </Button>

      <Typography variant="caption" color="text.secondary">
        This conversation is not saved. Leaving the page discards it.
      </Typography>
    </Stack>
  );
}
